import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import SignaturePad from './SignaturePad';
import { 
  AlertTriangle, Mic, MicOff, Loader2, 
  User, Calendar, Clock, MapPin, 
  FileText, Phone, CheckCircle, X
} from 'lucide-react';

const INCIDENT_TYPES = [
  { value: 'fall', label: 'Fall' },
  { value: 'medication_error', label: 'Medication Error' },
  { value: 'infusion_iv', label: 'Infusion/IV Issue' },
  { value: 'injury_unknown', label: 'Injury of Unknown Origin' },
  { value: 'change_in_condition', label: 'Change in Condition' },
  { value: 'equipment_failure', label: 'Equipment Failure' },
  { value: 'abuse_neglect', label: 'Abuse/Neglect/Exploitation' },
  { value: 'other', label: 'Other' }
];

const LOCATION_TYPES = [
  { value: 'client_home', label: "Client's Home" },
  { value: 'community', label: 'Community' },
  { value: 'other', label: 'Other' }
];

export default function IncidentReportForm({ client, employee, onComplete, onCancel }) {
  const [submitting, setSubmitting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const [signatureData, setSignatureData] = useState(null);
  
  const [formData, setFormData] = useState({
    // Pre-filled from props
    client_id: client?.id || '',
    client_name: client ? `${client.first_name} ${client.last_name}` : '',
    employee_id: employee?.id || '',
    employee_name: employee?.full_name || '',
    
    // Date/Time
    date_of_incident: new Date().toISOString().split('T')[0],
    time_of_incident: new Date().toTimeString().slice(0, 5),
    am_pm: new Date().getHours() >= 12 ? 'PM' : 'AM',
    
    // Location
    location_type: 'client_home',
    location_other: '',
    
    // Incident
    incident_type: '',
    incident_other: '',
    description: '',
    
    // Immediate Response
    first_aid_applied: false,
    emergency_services_called: false,
    emergency_time: '',
    physician_notified: false,
    physician_name: '',
    physician_datetime: '',
    family_notified: false,
    family_name: '',
    family_datetime: '',
    rn_assessment: false,
    rn_time: '',
    rn_phone: '',
    
    // Witness
    witness_name: '',
    
    // Analysis
    root_cause: '',
    corrective_action: '',
    followup_required: false,
    followup_date: ''
  });

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setFormData(prev => ({
            ...prev,
            description: prev.description + finalTranscript
          }));
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error('Speech recognition error. Please try again.');
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
      toast.info('Listening... Speak now.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.incident_type) {
      toast.error('Please select an incident type');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Please provide a description of the incident');
      return;
    }
    if (!signatureData) {
      toast.error('Please sign the report before submitting');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/shift-toolkit/incident-report', {
        ...formData,
        signature_data: signatureData
      });
      
      toast.success('Incident report submitted successfully!');
      onComplete?.();
    } catch (error) {
      console.error('Failed to submit report:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <Card className="p-6 border-red-200 bg-red-50">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-red-100 rounded-xl">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-red-900">Confidential Incident Report</h2>
            <p className="text-red-700">Document all incidents accurately and completely</p>
          </div>
        </div>
      </Card>

      {/* Auto-Filled Info */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Parties Involved
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Client Name</Label>
            <Input
              value={formData.client_name}
              disabled
              className="bg-gray-50"
            />
          </div>
          <div>
            <Label>Staff Member Reporting</Label>
            <Input
              value={formData.employee_name}
              disabled
              className="bg-gray-50"
            />
          </div>
        </div>
      </Card>

      {/* Date, Time, Location */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          When & Where
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label>Date of Incident *</Label>
            <Input
              type="date"
              name="date_of_incident"
              value={formData.date_of_incident}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <Label>Time of Incident *</Label>
            <Input
              type="time"
              name="time_of_incident"
              value={formData.time_of_incident}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <Label>AM/PM</Label>
            <select
              name="am_pm"
              value={formData.am_pm}
              onChange={handleChange}
              className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-purple-500"
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Location of Incident *</Label>
            <select
              name="location_type"
              value={formData.location_type}
              onChange={handleChange}
              className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-purple-500"
              required
            >
              {LOCATION_TYPES.map(loc => (
                <option key={loc.value} value={loc.value}>{loc.label}</option>
              ))}
            </select>
          </div>
          {formData.location_type === 'other' && (
            <div>
              <Label>Specify Location</Label>
              <Input
                name="location_other"
                value={formData.location_other}
                onChange={handleChange}
                placeholder="Describe location..."
              />
            </div>
          )}
        </div>
      </Card>

      {/* Incident Type */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Type of Incident *
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {INCIDENT_TYPES.map(type => (
            <label
              key={type.value}
              className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.incident_type === type.value
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <input
                type="radio"
                name="incident_type"
                value={type.value}
                checked={formData.incident_type === type.value}
                onChange={handleChange}
                className="w-4 h-4 text-red-600"
              />
              <span className={`text-sm ${formData.incident_type === type.value ? 'text-red-900 font-medium' : 'text-gray-700'}`}>
                {type.label}
              </span>
            </label>
          ))}
        </div>
        
        {formData.incident_type === 'other' && (
          <div className="mt-4">
            <Label>Specify Other Incident Type</Label>
            <Input
              name="incident_other"
              value={formData.incident_other}
              onChange={handleChange}
              placeholder="Describe the incident type..."
            />
          </div>
        )}
      </Card>

      {/* Description with Voice-to-Text */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Description of Incident *
        </h3>
        
        <div className="relative">
          <Textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={6}
            placeholder="Describe the incident in detail. Include what happened, how it happened, and any contributing factors..."
            className="pr-12"
            required
          />
          
          {/* Voice-to-Text Button */}
          {speechSupported && (
            <button
              type="button"
              onClick={toggleListening}
              className={`absolute right-3 top-3 p-2 rounded-full transition-all ${
                isListening 
                  ? 'bg-red-500 text-white animate-pulse' 
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
              title={isListening ? 'Stop listening' : 'Start voice input'}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
        </div>
        
        {isListening && (
          <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            Listening... Speak clearly into your microphone
          </div>
        )}
        
        {!speechSupported && (
          <p className="mt-2 text-sm text-gray-500">
            Voice input not supported in this browser. Please type your description.
          </p>
        )}
      </Card>

      {/* Immediate Response */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Immediate Response Actions
        </h3>
        
        <div className="space-y-4">
          {/* First Aid */}
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input
              type="checkbox"
              name="first_aid_applied"
              checked={formData.first_aid_applied}
              onChange={handleChange}
              className="w-5 h-5 text-purple-600 rounded"
            />
            <span className="font-medium">First Aid Applied</span>
          </label>
          
          {/* 911 */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                name="emergency_services_called"
                checked={formData.emergency_services_called}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium">911 / Emergency Services Called</span>
            </label>
            {formData.emergency_services_called && (
              <div className="ml-8">
                <Label>Time Called</Label>
                <Input
                  type="time"
                  name="emergency_time"
                  value={formData.emergency_time}
                  onChange={handleChange}
                  className="max-w-xs"
                />
              </div>
            )}
          </div>
          
          {/* Physician */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                name="physician_notified"
                checked={formData.physician_notified}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium">Physician Notified</span>
            </label>
            {formData.physician_notified && (
              <div className="ml-8 grid grid-cols-2 gap-4">
                <div>
                  <Label>Physician Name</Label>
                  <Input
                    name="physician_name"
                    value={formData.physician_name}
                    onChange={handleChange}
                    placeholder="Dr. ..."
                  />
                </div>
                <div>
                  <Label>Date/Time Notified</Label>
                  <Input
                    type="datetime-local"
                    name="physician_datetime"
                    value={formData.physician_datetime}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Family */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                name="family_notified"
                checked={formData.family_notified}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium">Family/Guardian Notified</span>
            </label>
            {formData.family_notified && (
              <div className="ml-8 grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    name="family_name"
                    value={formData.family_name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label>Date/Time Notified</Label>
                  <Input
                    type="datetime-local"
                    name="family_datetime"
                    value={formData.family_datetime}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* RN Assessment */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
              <input
                type="checkbox"
                name="rn_assessment"
                checked={formData.rn_assessment}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium">Client Assessment Performed by RN</span>
            </label>
            {formData.rn_assessment && (
              <div className="ml-8 grid grid-cols-2 gap-4">
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    name="rn_time"
                    value={formData.rn_time}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    name="rn_phone"
                    value={formData.rn_phone}
                    onChange={handleChange}
                    placeholder="Contact number"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Witness & Analysis */}
      <Card className="p-6 border-purple-100">
        <h3 className="font-semibold text-purple-900 mb-4">Additional Information</h3>
        
        <div className="space-y-4">
          <div>
            <Label>Witness Name (if any)</Label>
            <Input
              name="witness_name"
              value={formData.witness_name}
              onChange={handleChange}
              placeholder="Name of any witnesses"
            />
          </div>
          
          <div>
            <Label>Analysis of Root Cause</Label>
            <Textarea
              name="root_cause"
              value={formData.root_cause}
              onChange={handleChange}
              rows={3}
              placeholder="What factors contributed to this incident?"
            />
          </div>
          
          <div>
            <Label>Corrective Action Taken</Label>
            <Textarea
              name="corrective_action"
              value={formData.corrective_action}
              onChange={handleChange}
              rows={3}
              placeholder="What steps were taken to address the situation?"
            />
          </div>
          
          <div className="space-y-2">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="followup_required"
                checked={formData.followup_required}
                onChange={handleChange}
                className="w-5 h-5 text-purple-600 rounded"
              />
              <span className="font-medium">Follow-up Required</span>
            </label>
            {formData.followup_required && (
              <div className="ml-8">
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  name="followup_date"
                  value={formData.followup_date}
                  onChange={handleChange}
                  className="max-w-xs"
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Signature */}
      <SignaturePad 
        onSignatureChange={setSignatureData}
        label="Staff Member Signature"
      />

      {/* Actions */}
      <div className="flex justify-between items-center pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-gray-300"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        
        <Button
          type="submit"
          disabled={submitting}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="submit-incident-report"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Submit Incident Report
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
